import * as THREE from 'three';
import { SKINS, SKIN_TILE_PX, linearMean, skinTile, type Skin } from './skinTiles';
import { SPRITE_SYMBOLS, type Sprite } from '../ui/data/spriteTypes';

/**
 * The aquarium's pixel-art skins laid on its surfaces (M77), as Sable Reserve
 * lays its terrain tiles (its M354): the tiles in one array texture,
 * magnified with no smoothing so their pixels stay square, mipmapped far off
 * so they do not shimmer, laid in the scene's own space at a fixed texel
 * size, from above on what faces up and from the side on what faces sideways,
 * so neither is drawn stretched.
 *
 * **Over the color the data gives, not instead of it.** The seafloor's
 * color says its depth, the land's its height, the reef's its coral and its
 * bleaching (`aquariumView.ts`). Each tile is divided by its own mean color
 * in linear light before it is laid on (`linearMean`), so on average it
 * changes nothing and adds only its pattern: the box reads as it did, and
 * close up it is sand, coral heads, scrub and stacked lava.
 *
 * **Which tile a pixel takes.** On the seafloor, each texel is the land's
 * where its patch's draw falls under the share of land at that point (1 on
 * land, 0 at sea, between them along a shore), else the reef's where it
 * falls under the reef's share of the floor, else the user's sand: so a
 * shore and a reef's edge are drawn as irregular patches of the two tiles
 * (`PATCH_TEXELS`, M105; each texel's own draw until then, which up close
 * read as a checker), and not as a line through the triangles. A surface
 * with one skin (the cutaway's rock; the user's own tile on the flat floor
 * of a box with no seafloor, and on the water's surface) takes it
 * everywhere.
 */

/**
 * The seafloor's patches (M105, the redesign v2's "irregular, coherent patches", "no tiny high-contrast checker"):
 * which tile a texel takes, rock or sand, and which of the paint's colors, drawn by its patch, about `PATCH_TEXELS`
 * texels across (JUDGED: at the Reef's shot on a phone a texel is about 4 pixels, a patch about 25), its edges warped
 * by `PATCH_WARP` of a patch (JUDGED: irregular, no grid), each patch one draw, so the share of them under a share is
 * that share, as each texel's was; the colors drifting over a patch's width, dithered at their edges by `PATCH_DITHER`
 * of the texel's own draw (JUDGED).
 */
const PATCH_TEXELS = 6;
const PATCH_WARP = 1.2;
const PATCH_DITHER = 0.25;

/** Bytes the tiles hold on the graphics card, mipmaps and all. */
export function skinTextureBytes(): number {
  let bytes = 0;
  for (let size = SKIN_TILE_PX; size >= 1; size = Math.floor(size / 2)) bytes += size * size * 4;
  return bytes * SKINS.length;
}

/** The tiles as one array texture, a layer a skin in `SKINS`'s order, turned over since a texture's first row is its bottom, and their means. */
export function skinTexture(): { texture: THREE.DataArrayTexture; means: THREE.Vector3[] } {
  const n = SKIN_TILE_PX;
  const data = new Uint8Array(n * n * 4 * SKINS.length);
  const means = SKINS.map((skin, layer) => {
    const tile = skinTile(skin);
    for (let y = 0; y < n; y++) data.set(tile.subarray((n - 1 - y) * n * 4, (n - y) * n * 4), (layer * n * n + y * n) * 4);
    return new THREE.Vector3(...linearMean(tile));
  });
  const texture = new THREE.DataArrayTexture(data, n, n, SKINS.length);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return { texture, means };
}

/**
 * A tile of the user's own, at its own size (the ripple's, read from their
 * picture by `scripts/tileArt.ts`, `npm run tile-art`; `scripts/ripple.ts` until review M116 named a script that is not there): its pixels as a texture, magnified with no
 * smoothing and repeating, as the drawn tiles are, and its mean color in
 * linear light, which the surface divides by.
 */
export interface OwnTile {
  texture: THREE.DataTexture;
  mean: THREE.Vector3;
  width: number;
  height: number;
}

/** A sprite's pixels, every one drawn (a tile has no clear pixel), as an `OwnTile`; turned over, a texture's first row its bottom. */
export function ownTile(tile: Sprite): OwnTile {
  const colors = tile.palette.map((hex) => [1, 3, 5].map((k) => parseInt(hex.slice(k, k + 2), 16)));
  const rgba = new Uint8Array(tile.width * tile.height * 4);
  tile.rows.forEach((row, y) => [...row].forEach((symbol, x) => {
    const color = colors[SPRITE_SYMBOLS.indexOf(symbol)];
    if (!color) throw new Error(`the tile's pixel ${x}, ${y} is '${symbol}', not one of its colors`);
    rgba.set([...color, 255], ((tile.height - 1 - y) * tile.width + x) * 4);
  }));
  const texture = new THREE.DataTexture(rgba, tile.width, tile.height);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.NearestFilter;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.generateMipmaps = true;
  texture.needsUpdate = true;
  return { texture, mean: new THREE.Vector3(...linearMean(rgba)), width: tile.width, height: tile.height };
}

/** The tiles, drawn once a page: every view a load builds shares them (`AquariumView.dispose` leaves them be). */
let shared: { texture: THREE.DataArrayTexture; means: THREE.Vector3[] } | null = null;
function sharedSkins(): { texture: THREE.DataArrayTexture; means: THREE.Vector3[] } {
  shared ??= skinTexture();
  return shared;
}

export interface SkinOptions {
  /** Scene units a texel spans. */
  texel: number;
  /**
   * Scene units a texel of the caustics' web spans, the web's tile laid on at it: the texel's unless said (M180: the
   * Reef's ground's texel halved, its caustics' web kept as it was, the user's word being the ground's pixels alone).
   */
  causticTexel?: number;
  /** One skin everywhere; or, when absent, the seafloor's choice by the geometry's `aSkinLand` and `aSkinReef`. */
  only?: Skin;
  /** A tile of the user's own everywhere, at its own size, in place of the drawn ones (the ripple's). */
  own?: OwnTile;
  /**
   * The floor's sand, the user's own picture (`render/data/sand.ts`), where
   * the seafloor's choice (no `only`) picks it; its land and reef stay the
   * drawn tiles'.
   */
  floor?: { sand: OwnTile };
  /**
   * Also lays the tile on the surface's opacity, by its brightness against its
   * mean's, so a faint translucent pane (the water's surface) shows its pattern
   * and keeps its opacity on average.
   */
  alpha?: boolean;
  /**
   * Caustics on a floor: the caustic tile laid on as light, twice, each copy
   * drifting its own way, by `aCaustic`, the share of the sun's light that
   * reaches the vertex (`AquariumView.setDay` writes it from the measured
   * clarity), so they fade where the light does.
   */
  caustics?: boolean;
  /** Fog: the surface's color fades toward the water's by how far the eye looks through the water to see it. */
  fog?: boolean;
  /**
   * The exhibit's floor (M94): in place of the sand's picture and the reef's tile, each texel takes a color of a
   * small palette (`ExhibitPaint`), crisp as pixel art: basalt where the real floor is steep (`aSlope`, degrees),
   * sand going to silt with depth (`aDepth`, m), reef pavement with coralline flecks where the reef's share
   * picks it, over the color its coral gives; the land keeps its tile. Each is lit as the water lights it,
   * `aWater` (a color to multiply by, from the day's light at its depth).
   */
  paint?: ExhibitPaint;
}

/** The share of the reef's pavement drawn as coralline's clumps (M105: as much of it as the flecks had been), its own
 * coral painted on the rest (M125). */
const CORALLINE_SHARE = 0.16;

/** The exhibit's palettes, linear colors, four of each but the coralline's two; and the real slope rock begins and ends at, degrees. */
export interface ExhibitPaint {
  sand: readonly THREE.Color[];
  silt: readonly THREE.Color[];
  basalt: readonly THREE.Color[];
  coralline: readonly THREE.Color[];
  rockFromDeg: number;
  rockToDeg: number;
  /** The depths, m, over which sand gives way to silt. */
  siltFromM: number;
  siltToM: number;
  /**
   * The pavement's own coral (M125, Maui Nui Reef's): its live colors and the bleached one, laid in clumps half a patch
   * across over the share of a point's pavement `aCoral`'s first gives (of the clumps the coralline leaves, 84%),
   * bleached over its second's share of them, `pale` of those halfway to white and the rest white, as the colonies are
   * (`PALE_SHARE`); none where absent (the whole tank's).
   */
  coral?: { live: readonly THREE.Color[]; bleached: THREE.Color; pale: number };
  /** Its patches' size, texels, and their edges' warp, a patch's share (`PATCH_TEXELS` and `PATCH_WARP` unless said). */
  patchTexels?: number;
  patchWarp?: number;
  /**
   * Its edges broken up (M174, Maui Nui Reef's: the user's "transitions and edges look too hard, blocky, and
   * artificial"; none unless said): `breakup` a second warp half a patch across, its share of the patch's warp, so a
   * patch's outline wanders at two sizes; `jitter` the span, in texels, each texel reads its patch from a point of its own
   * within (half of it either way) (sand and rock, sand and pavement, the coralline's and the coral's clumps), so a boundary is a ragged fringe
   * of single pixels and a patch's inside stays whole, each surface's share kept (every texel moved alike on average).
   */
  edges?: { breakup: number; jitter: number };
  /**
   * The caustics' brightness varying over the floor (M174: the user's "natural variation in brightness"; none unless
   * said): their contrast `amount` either way of its own, by smooth noise `texels` across, still as the web drifts.
   */
  causticVary?: { amount: number; texels: number };
  /**
   * Maui Nui Reef's materials (M177, the v5 brief's "Hawaiian Reef Color Palette"; none unless said, the whole tank's
   * paint): each material's colors as five tones (the brief's roles, highlight to deepest, sRGB before the water's light,
   * the user's word: "the palette should describe the materials before underwater lighting is applied"; the rubble's
   * four). A texel of hard bottom is limestone or basalt by its point's geology (`aSub`'s w, its edge wandering by the
   * material's drift, so a formation is one material), its tone the ramp's by its form: its face's tilt (`tilt` tones from
   * flat to a wall), its recess (`aRecess`, `recess` tones at the deepest), the material's own drift (`drift` tones
   * either way, by the patches' warp), horizontal strata on its walls (`strata`: a band `bandM` apart, `width` of it, a
   * tone darker), and cracks (`crack`: the patches' warp's lines, `width` of it, `share` of the regions, at the deepest
   * tone but one); a little dither (`dither` tones) only where two tones meet. Over it, by the
   * surveys: the turf as a thin film (`aSub`'s x, `film` of the way to its tone) on what faces up, the coralline in small
   * clumps (`aSub`'s y, `clump` of a patch) on what faces sideways, and rubble (`aSub`'s z) in patches, its grains `rubbleTexels` across. Sand its own
   * five, brighter shallow and cooler deep (`sandDeep` tones from `sandAtM` to 30 m), in a recess darker, with faint
   * ripples (`ripple`: crests `periodM` apart, a tone lighter) and stones (`stones`: of the texels within `nearReef` of
   * hard bottom, clusters of `texels`) in the rubble's tones; its caustics `sandCaustics` as strong as the rock's.
   */
  reef?: ReefPaint;
}

/**
 * The paint's smooth noise's quantiles at sixteenths (M246, `skinNoise` sampled: its value with that share of the texels
 * under it), its ends widened past 0 and 1 so a share of none and of all are exact; MEASURED by sampling the same noise,
 * corners uniform and blended by smoothstep, over 3,200 by 3,200 points (`tests/floorLook.test.ts` measures them again).
 */
export const NOISE_QUANTILES: readonly number[] = [-0.001, 0.166, 0.2354, 0.2896, 0.3373, 0.3808, 0.4216, 0.461, 0.4999, 0.539, 0.5784, 0.6191, 0.6628, 0.7108, 0.7652, 0.8346, 1.001];
/** The same for the patches' warp with its edges broken up half a patch (`skinWarp.x`: a noise less a half, and half another half its size), M246. */
export const WARP_QUANTILES: readonly number[] = [-0.751, -0.3684, -0.2867, -0.2266, -0.1752, -0.1283, -0.0841, -0.0415, 0.0004, 0.0425, 0.0852, 0.1298, 0.177, 0.2289, 0.2892, 0.3709, 0.751];

/** Maui Nui Reef's materials (`ExhibitPaint.reef`, M177): linear colors, the limestone's six (M246), five a material but the rubble's four. */
export interface ReefPaint {
  limestone: readonly THREE.Color[];
  basalt: readonly THREE.Color[];
  sand: readonly THREE.Color[];
  turf: readonly THREE.Color[];
  coralline: readonly THREE.Color[];
  rubble: readonly THREE.Color[];
  base: number;
  tilt: number;
  recess: number;
  drift: number;
  strata: { bandM: number; width: number };
  crack: { width: number; share: number };
  dither: number;
  cobble: { texels: number; tones: number };
  lift: number;
  balance: number;
  film: number;
  clump: number;
  sandBase: number;
  sandAtM: number;
  sandDeep: number;
  ripple: { periodM: number; width: number };
  stones: { nearReef: number; texels: number; share: number };
  rubbleTexels: number;
  sandCaustics: number;
  /**
   * The ground's small relief (M246, the v6 brief's "Represent smaller-scale uneven ground", "simulate fine surface
   * relief in the material shader instead of adding unnecessary geometry"): a height over the face, m, from the paint's
   * own smooth noises (its patches' warp `amp[1]`, its edges' breakup `amp[2]`, both a texel's hashes already, and one
   * of its own `bigTexels` across, `amp[0]`), each cobble tilted `cobbleTilt` either way and the sand's ripples
   * `ripple` m high; its slope bends the face's normal, a texel at a time, so the sun's key light (the hour's direction
   * and strength) lights what faces it and leaves what faces away, and the moon's at night all but nothing. Its own
   * tones, which do not move with the sun: a raised texel `highlight` m over its mean on what faces up takes the
   * limestone's brightest tone, a hollow `cavity` m under it a tone darker; the rest of the limestone a tone on, lifted
   * `stoneLift` (its first tone's luminance over its second's, MEASURED from the palette: 1.247).
   */
  relief: { bigTexels: number; amp: readonly [number, number, number]; cobbleTilt: number; ripple: number; highlight: number; cavity: number; stoneLift: number };
  /** The coralline's film (M246): its patches the hard bottom's color taken that much of the way to its tone. */
  corallineFilm: number;
}

/**
 * A painted floor's eye (M169): its water's light divided by this, the water's color at the eye's own depth, so the eye
 * is balanced to the light about it as a diver's is (Maui Nui Reef's; white, none, unless set).
 */
export interface PaintUniforms {
  uExAdapt: { value: THREE.Color };
}

/** The caustics' uniforms (`SkinOptions.caustics`). */
export interface CausticUniforms {
  /** The two copies' slides, scene units: x and y the first's, z and w the second's; laid on as they are (M227). */
  uCausticDrift: { value: THREE.Vector4 };
  /**
   * How far the floor is lifted where both copies' brightest lines (the tile's
   * white) cross and all the light reaches: the copies are laid on as their
   * mean, so one line alone lifts it about half that, and the darkest cells dim
   * it by the share the tile's mean sets, so the web lights nothing on average.
   */
  uCausticContrast: { value: number };
  /** The water's surface, scene units: no caustics above it, where a shore's triangle reaches onto the beach. */
  uCausticSurface: { value: number };
}

/** The fog's uniforms (`SkinOptions.fog`). */
export interface FogUniforms {
  /** The water's box, world space. */
  uFogBoxMin: { value: THREE.Vector3 };
  uFogBoxMax: { value: THREE.Vector3 };
  /** Per scene unit of water looked through. */
  uFogDensity: { value: number };
  /** What the fog fades toward, linear. */
  uFogColor: { value: THREE.Color };
  /**
   * What it fades toward in the deep (M94), from the height `uFogDeepAt.x` (where it is `uFogColor`) down to
   * `uFogDeepAt.y` (where it is this), and how many times denser it is there; the map's the same as its own
   * color, once as dense.
   */
  uFogDeep: { value: THREE.Color };
  uFogDeepAt: { value: THREE.Vector2 };
  uFogDeepDensity: { value: number };
  /**
   * The veil's curve (M169): 1 its share of what lies d away 1 - exp(-density d), the water's own; 2 the same at 1 /
   * density but 1 - exp(-(density d)^2), lighter nearer and heavier beyond (Maui Nui Reef's, the v4 brief's "atmospheric
   * haze primarily in the distance").
   */
  uFogPower: { value: number };
}

/**
 * The water's fog in GLSL, one chunk for every fogged shader (M182: the floor's skin, `fogOnly`'s plain materials, the
 * sprites' batch and the particles had each carried a copy): its uniforms (`FogUniforms`) and `waterFog`, what the
 * water lays over a point at `world` seen from the camera, its color and how much of the point it hides. The stretch of
 * the eye's line to the point that runs through the water's box, by the slab method; darker and denser with depth,
 * where it is set so (M94: the map's deep is its own color, once as dense); on the veil's curve (`uFogPower`, M169).
 */
export const WATER_FOG = /* glsl */ `
uniform vec3 uFogBoxMin;
uniform vec3 uFogBoxMax;
uniform float uFogDensity;
uniform vec3 uFogColor;
uniform vec3 uFogDeep;
uniform vec2 uFogDeepAt;
uniform float uFogDeepDensity;
uniform float uFogPower;
vec4 waterFog(vec3 world) {
  vec3 fogRay = world - cameraPosition;
  vec3 fogStep = fogRay + vec3(1e-6);
  vec3 fogA = (uFogBoxMin - cameraPosition) / fogStep;
  vec3 fogB = (uFogBoxMax - cameraPosition) / fogStep;
  // Named apart from three.js's own linear fog, whose near and far uniforms share the scope (M78's reviewer).
  vec3 slabNear = min(fogA, fogB);
  vec3 slabFar = max(fogA, fogB);
  float fogIn = max(max(slabNear.x, slabNear.y), max(slabNear.z, 0.0));
  float fogOut = min(min(slabFar.x, slabFar.y), min(slabFar.z, 1.0));
  float fogWater = max(0.0, fogOut - fogIn) * length(fogRay);
  float fogDeep = clamp((uFogDeepAt.x - world.y) / (uFogDeepAt.x - uFogDeepAt.y), 0.0, 1.0);
  return vec4(mix(uFogColor, uFogDeep, fogDeep), 1.0 - exp(-pow(uFogDensity * mix(1.0, uFogDeepDensity, fogDeep) * fogWater, uFogPower)));
}
`;

/** A fog's uniforms before the water gives it any: its density none. */
export function noFog(): FogUniforms {
  return {
    uFogBoxMin: { value: new THREE.Vector3() }, uFogBoxMax: { value: new THREE.Vector3() }, uFogDensity: { value: 0 }, uFogColor: { value: new THREE.Color() },
    uFogDeep: { value: new THREE.Color() }, uFogDeepAt: { value: new THREE.Vector2(1, 0) }, uFogDeepDensity: { value: 1 }, uFogPower: { value: 1 },
  };
}

/** What a skinned material's shader is given, for the tests and for turning the skins off. */
export interface SkinUniforms {
  uSkinTiles: { value: THREE.DataArrayTexture };
  uSkinMeans: { value: THREE.Vector3[] };
  uSkinScale: { value: number };
  uSkinTexel: { value: number };
  /** How much of the tile is laid on, 0 to 1. */
  uSkinStrength: { value: number };
  /**
   * How far the tile has slid, scene units along the surface's two axes (the
   * ripple's drift, M77): laid on in whole texels, so the pixels step as pixel
   * art moves and never smear.
   */
  uSkinDrift: { value: THREE.Vector2 };
}

/** A coral clump's color on the pavement (M125), the tank's and the Reef's paint alike: each clump one live color,
 *  white over the bleached share of them, a little rough by texel. */
function coralClump(at: string, halfPatch: string, warp: string, coral: NonNullable<ExhibitPaint['coral']>): string {
  return `vec2 exClump = floor(${at} / ${halfPatch} + exWarp * ${warp});
            int exK = int(clamp(floor(skinHash(exClump, 18u) * ${coral.live.length}.0), 0.0, ${coral.live.length - 1}.0));
            vec3 exLive = uExCoral[exK];
            exColor = (skinHash(exClump, 19u) < vExCoral.y ? (skinHash(exClump, 21u) < ${coral.pale.toFixed(4)} ? mix(exLive, uExBleached, 0.5) : uExBleached) : exLive) * (0.88 + 0.24 * skinHash(skinTexel, 20u));`;
}

/**
 * Maui Nui Reef's paint (M177, `ExhibitPaint.reef`), GLSL run before the whole tank's: a texel of sand or of hard bottom
 * takes its material's tone (skinOwn 2), so the whole tank's branches below pass it by. Its sand is what the seafloor's
 * choice left sand and not steep (the whole tank's rock there taken as hard bottom); the rest, the reef's pavement and
 * the steep, hard bottom. Its form read from the face's tilt, taken outside any branch (`skinFacing`), and its point's
 * recess (`aRecess`).
 */
function reefPaint(r: ReefPaint, at: string, patch: string, halfPatch: string, warp: string, coral: ExhibitPaint['coral'] | null, reefLayer: string, f1: (v: number) => string): string {
  const rl = r.relief;
  return `if (skinOwn == 1 || skinLayer == ${reefLayer}) {
          // How much the face looks up by its smooth normal (M246: by the triangle's own, the tones broke into its facets).
          float rfUpness = abs(normalize((vec4(vNormal, 0.0) * viewMatrix).xyz).y);
          // The texel's own draw for its growths and its rock (M246): a smooth noise, warped by the patches' own, its
          // share's quantile the line (\`skinNoiseAt\`), so each is drawn in irregular blobs, not square patches; read at
          // the texel's own point (\`${at}\`), so their edges fray in single pixels as the patches' did (M174).
          float rfBio = skinNoise(${at} + 4.0 * skinWarp, ${patch}, 61u);
          // Its small relief (M246): a height, m, from the paint's own noises, its slope per meter along the face.
          vec3 rfBig = skinNoiseD(skinTexel, ${f1(rl.bigTexels)}, 62u);
          // The steep rock against the sand by the patches' drift (its warp's other half), a field of its own: read from the
          // growths' own draw, the turf and the rubble on that rock had been cut to what their shares overlapped (review
          // M246's: a face half rock, its turf's 30% none).
          bool rfSand = skinOwn == 1 && !(skinWarp.y > skinWarpAt(1.0 - smoothstep(uExRock.x, uExRock.y, vExSlope)));
          skinOwn = 2;
          float rfHeight = ${f1(rl.amp[0])} * (rfBig.x - 0.5) + ${f1(rl.amp[1])} * (skinWarpD.x - 0.5) + ${f1(rl.amp[2])} * (skinBreakD.x - 0.5);
          vec2 rfSlope = (${f1(rl.amp[0])} * rfBig.yz + ${f1(rl.amp[1])} * skinWarpD.yz + ${f1(rl.amp[2])} * skinBreakD.yz) / uSkinTexel;
          if (rfSand) {
            // Sand: brighter shallow, cooler deep and in a recess beside the rock, drifting a little, faint ripples.
            float rfS = ${f1(r.sandBase)} + ${f1(r.sandDeep)} * clamp((vExDepth - ${f1(r.sandAtM)}) / (30.0 - ${f1(r.sandAtM)}), 0.0, 1.0) + ${f1(r.recess)} * pow(max(vExRecess, 0.0), 1.3)
              + ${f1(r.drift)} * 2.0 * skinWarp.y;
            float rfRipple = fract(dot(vSkinAt.xz, vec2(0.8, 0.6)) / ${f1(r.ripple.periodM)} + 1.5 * skinWarp.x);
            if (rfRipple < ${f1(r.ripple.width)}) rfS -= 0.35;
            // Each ripple rising to its crest and falling (M246): its faces lit by the sun as they face it.
            rfSlope = 0.5 * rfSlope + (rfRipple < 0.5 ? 1.0 : -1.0) * ${f1((2 * rl.ripple) / r.ripple.periodM)} * vec2(0.8, 0.6);
            rfS += ${f1(r.dither)} * (skinHash(skinTexel, 45u) - 0.5);
            exColor = uRfSand[int(clamp(floor(rfS), 0.0, 4.0))];
            // Stones in the sand near the rock, a few texels each, in the rubble's tones.
            vec2 rfStone = floor(${at} / ${f1(r.stones.texels)});
            if (skinHash(rfStone, 53u) < ${f1(r.stones.share)} * smoothstep(0.0, ${f1(r.stones.nearReef)}, vSkinReef)) exColor = uRfRubble[int(clamp(floor(skinHash(rfStone, 54u) * 2.0 + 2.0 * vExRecess + skinHash(skinTexel, 55u) * 0.9), 0.0, 3.0))];
            rfCaustic = ${f1(r.sandCaustics)} * (1.0 - 0.7 * vExRecess);
          } else {
            // Hard bottom: one material by its geology, its tone by its form.
            // Its cobbles: clusters of a few texels, their outlines warped, each a little lighter or darker (the brief's
            // "carefully chosen color clusters rather than random pixel noise").
            // (Its outline warped by the patches' own warp, its drift and its cracks read from that same smooth noise, used
            // again for the strata's wander, the geology's edge and the coralline's clusters: a noise of its own is four
            // hashes a texel, MEASURED in M177 at a third of what its paint first added to a software frame for four.)
            vec2 rfCobble = floor((skinTexel + 1.6 * ${f1(r.cobble.texels)} * skinWarp) / ${f1(r.cobble.texels)});
            // Each cobble a facet of its own, tilted a little (M246): embedded stones catching the light.
            rfSlope += ${f1(rl.cobbleTilt)} * 2.0 * (vec2(skinHash(rfCobble, 63u), skinHash(rfCobble, 64u)) - 0.5);
            float rfDrift = 0.5 + skinWarp.y;
            float rfTone = ${f1(r.base)} + ${f1(r.tilt)} * (1.0 - rfUpness) + ${f1(r.recess)} * pow(max(vExRecess, 0.0), 1.3) + ${f1(r.drift)} * 2.0 * (rfDrift - 0.5)
              + ${f1(r.cobble.tones)} * (skinHash(rfCobble, 58u) - 0.5);
            if (rfUpness < 0.8 && fract(vSkinAt.y / ${f1(r.strata.bandM)} + 0.7 * rfDrift) < ${f1(r.strata.width)}) rfTone += 0.8;
            if (abs(skinWarp.x + 0.35 * skinWarp.y) < ${f1(r.crack.width)} && fract(rfDrift * 7.0) < ${f1(r.crack.share)}) rfTone = max(rfTone + 1.2, 3.0);
            // A hollow a tone darker, what does not move with the sun (M246): the brief's "Small depressions receive
            // softer shadows".
            if (rfHeight < -${f1(rl.cavity)}) rfTone += 1.0;
            float rfDither = skinHash(skinTexel, 45u) - 0.5;
            rfTone += ${f1(r.dither)} * rfDither;
            int rfI = int(clamp(floor(rfTone), 0.0, 5.0));
            // A raised texel facing up a tone brighter (M246): "Distinctive highlights on uneven upper surfaces".
            int rfUp = rfHeight > ${f1(rl.highlight)} && rfUpness > 0.7 ? 1 : 0;
            // Basalt or limestone by the geology, its edge wandering by smooth noise (an organic boundary, not a patch's).
            float rfGeology = vExSub.w + 0.3 * (rfDrift - 0.5) + ${f1(r.dither)} * 0.3 * rfDither;
            bool rfIsBasalt = rfGeology > 0.5;
            // The limestone's six tones, its brightest a raised texel's alone (review M246's: the flat rock's own at first,
            // the highlight then nothing), the rest a tone on, lifted by the two tones' ratio (\`relief.stoneLift\`) so the
            // rock stands as bright under the water's light; a raised basalt texel a tone brighter. The growths' ramps five.
            exColor = rfIsBasalt ? uRfBasalt[clamp(rfI - rfUp, 0, 4)] : uRfStone[clamp(rfI + 1 - rfUp, 0, 5)] * ${f1(rl.stoneLift)};
            rfI = min(rfI, 4);
            // Its clumps' outlines the patches' warp, which wanders at half a patch already (\`edges.breakup\`).
            vec2 exWarp = skinWarp;
            ${coral ? `// The pavement's own coral first (M125), as the data asks, the growths only where it is not; on the
            // pavement alone, as the scene counts it (M177's review: on the sand's steep rock too, 5% over). In blobs of
            // the texel's own draw at their share (M246).
            if (skinLayer == ${reefLayer} && rfBio > skinNoiseAt(1.0 - vExCoral.x)) {
              ${coralClump(at, halfPatch, warp, coral)}
            } else` : ''} if (rfBig.x > skinNoiseAt(1.0 - vExSub.y * (1.6 - 1.2 * rfUpness))) {
              // The coralline on what faces sideways, a film over the rock (M246: solid, its squares had read as camouflage);
              // by the relief's own noise, on the raised rock, a field apart from the pavement's (review M246's: by its
              // warp, it had taken all the pavement's coralline-free texels as its share reached the pavement's).
              exColor = mix(exColor, uRfCoralline[rfI], ${f1(r.corallineFilm)});
            } else {
              if (!rfIsBasalt && rfBio < skinNoiseAt(vExSub.x * 1.6 * rfUpness * rfUpness * rfUpness)) exColor = mix(exColor, uRfTurf[rfI], ${f1(r.film)});
              // Rubble in pockets, more in a recess: the texel's draw nearest its middle.
              else if (abs(rfBio - 0.5) < skinNoiseAt(0.5 + 0.5 * min(1.0, vExSub.z * (0.6 + vExRecess))) - 0.5) exColor = uRfRubble[int(clamp(floor(skinHash(floor(skinTexel / ${f1(r.rubbleTexels)}), 50u) * 2.0 + 0.5 * float(rfI)), 0.0, 3.0))];
            }
            rfCaustic = 1.0 - 0.7 * vExRecess;
          }
          // The slope along the face's two axes as the paint lays it (\`skinUv\`), in the world.
          skinBump = skinFacing.y >= max(skinFacing.x, skinFacing.z) ? vec3(rfSlope.x, 0.0, rfSlope.y)
            : (skinFacing.x >= skinFacing.z ? vec3(0.0, rfSlope.y, rfSlope.x) : vec3(rfSlope.x, rfSlope.y, 0.0));
          exColor *= ${f1(r.lift)};
        }
        `;
}

/**
 * Patches a material to lay the skins over its color. Anchored after
 * `<color_fragment>`, so the tile multiplies the vertex color the data gave;
 * the face's direction is read from the screen-space change of its position,
 * so a material with no normals (the cutaway's) is laid the same way.
 */
export function attachSkin(material: THREE.Material, options: SkinOptions): SkinUniforms & Partial<CausticUniforms & FogUniforms & PaintUniforms> {
  const { texture, means } = sharedSkins();
  const uniforms: SkinUniforms = {
    uSkinTiles: { value: texture },
    uSkinMeans: { value: means },
    uSkinScale: { value: 1 / (options.texel * SKIN_TILE_PX) },
    uSkinTexel: { value: options.texel },
    uSkinStrength: { value: 1 },
    uSkinDrift: { value: new THREE.Vector2() },
  };
  const own = options.own ?? null;
  const ownUniforms = own ? {
    uSkinOwn: { value: own.texture },
    uSkinOwnMean: { value: own.mean },
    uSkinOwnScale: { value: new THREE.Vector2(1 / (options.texel * own.width), 1 / (options.texel * own.height)) },
  } : null;
  const caustics: CausticUniforms | null = options.caustics
    ? { uCausticDrift: { value: new THREE.Vector4(0, 0, 0, 0) }, uCausticContrast: { value: 0 }, uCausticSurface: { value: 0 } }
    : null;
  const fog: FogUniforms | null = options.fog ? noFog() : null;
  const floor = options.only === undefined && !own ? options.floor ?? null : null;
  const paint = options.only === undefined && !own ? options.paint ?? null : null;
  if (options.only === undefined && !own && !floor && !paint) throw new Error('a seafloor skinned without its sand');
  const paintUniforms = paint ? {
    uExSand: { value: [...paint.sand] },
    uExSilt: { value: [...paint.silt] },
    uExBasalt: { value: [...paint.basalt] },
    uExCoralline: { value: [...paint.coralline] },
    uExRock: { value: new THREE.Vector2(paint.rockFromDeg, paint.rockToDeg) },
    uExSilting: { value: new THREE.Vector2(paint.siltFromM, paint.siltToM) },
    uExAdapt: { value: new THREE.Color(1, 1, 1) },
    ...(paint.coral ? { uExCoral: { value: [...paint.coral.live] }, uExBleached: { value: paint.coral.bleached } } : {}),
    ...(paint.reef ? { uRfStone: { value: [...paint.reef.limestone] }, uRfBasalt: { value: [...paint.reef.basalt] }, uRfSand: { value: [...paint.reef.sand] }, uRfTurf: { value: [...paint.reef.turf] }, uRfCoralline: { value: [...paint.reef.coralline] }, uRfRubble: { value: [...paint.reef.rubble] } } : {}),
  } : null;
  const patch = (paint?.patchTexels ?? PATCH_TEXELS).toFixed(1);
  const halfPatch = ((paint?.patchTexels ?? PATCH_TEXELS) / 2).toFixed(1);
  const warp = (paint?.patchWarp ?? PATCH_WARP).toFixed(2);
  const coral = paint?.coral ?? null;
  const edges = paint?.edges ?? null;
  const vary = paint?.causticVary ?? null;
  const reef = paint?.reef ?? null;
  const f1 = (v: number): string => v.toFixed(3);
  // The caustics' texel and the web's scale at it, the skin's own unless a texel of their own is given.
  const causticTexel = options.causticTexel ?? options.texel;
  const causticOwn = causticTexel !== options.texel;
  // The texel its patches are read at: its own, or a point of its own about it where the edges are broken up.
  const at = edges ? 'skinAt' : 'skinTexel';
  const floorUniforms = floor ? {
    uSkinSand: { value: floor.sand.texture },
    uSkinSandMean: { value: floor.sand.mean },
    uSkinSandScale: { value: new THREE.Vector2(1 / (options.texel * floor.sand.width), 1 / (options.texel * floor.sand.height)) },
  } : null;
  // A tile of the user's own takes no layer of the drawn ones: 0 is any, never read.
  const fixed = own ? 0 : options.only === undefined ? -1 : SKINS.indexOf(options.only);
  const layer = (skin: Skin): string => `${SKINS.indexOf(skin)}`;
  material.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms, caustics, fog, ownUniforms, floorUniforms, paintUniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
      varying vec3 vSkinAt;
      ${caustics ? 'attribute float aCaustic;\nvarying float vCaustic;' : ''}
      ${fog ? 'varying vec3 vSkinWorld;' : ''}
      ${fixed < 0 ? 'attribute float aSkinLand;\nattribute float aSkinReef;\nvarying float vSkinLand;\nvarying float vSkinReef;' : ''}
      ${paint ? 'attribute float aSlope;\nattribute float aDepth;\nattribute vec3 aWater;\nvarying float vExSlope;\nvarying float vExDepth;\nvarying vec3 vExWater;' : ''}
      ${coral ? 'attribute vec2 aCoral;\nvarying vec2 vExCoral;' : ''}
      ${reef ? 'attribute vec4 aSub;\nattribute float aRecess;\nvarying vec4 vExSub;\nvarying float vExRecess;' : ''}`)
      .replace('#include <begin_vertex>', `#include <begin_vertex>
      vSkinAt = transformed;
      ${caustics ? 'vCaustic = aCaustic;' : ''}
      ${fog ? 'vSkinWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;' : ''}
      ${fixed < 0 ? 'vSkinLand = aSkinLand;\nvSkinReef = aSkinReef;' : ''}
      ${paint ? 'vExSlope = aSlope;\nvExDepth = aDepth;\nvExWater = aWater;' : ''}
      ${coral ? 'vExCoral = aCoral;' : ''}
      ${reef ? 'vExSub = aSub;\nvExRecess = aRecess;' : ''}`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
      uniform highp sampler2DArray uSkinTiles;
      uniform vec3 uSkinMeans[${SKINS.length}];
      uniform float uSkinScale;
      uniform float uSkinTexel;
      uniform float uSkinStrength;
      uniform vec2 uSkinDrift;
      ${own ? 'uniform sampler2D uSkinOwn;\nuniform vec3 uSkinOwnMean;\nuniform vec2 uSkinOwnScale;' : ''}
      ${floor ? 'uniform sampler2D uSkinSand;\nuniform vec3 uSkinSandMean;\nuniform vec2 uSkinSandScale;' : ''}
      varying vec3 vSkinAt;
      ${caustics ? `uniform vec4 uCausticDrift;
      uniform float uCausticContrast;
      uniform float uCausticSurface;
      varying float vCaustic;
      // The caustic web's brightness at \`uv\` (tiles), blended between its texels (M227, the user's word: "caustics dont
      // look smooth."): the tiles are sampled nearest, every other skin's pixels kept square, so the web is read at its four
      // nearest texels' middles and mixed by where \`uv\` falls between them. Light, unlike rock and sand, has no edge of
      // its own to keep.
      float causticWeb(vec2 uv, float layer, float dx, float dy) {
        const vec3 webLuma = vec3(0.2126, 0.7152, 0.0722);
        vec2 t = uv * ${SKIN_TILE_PX.toFixed(1)} - 0.5;
        vec2 f = fract(t);
        vec2 b = (floor(t) + 0.5) / ${SKIN_TILE_PX.toFixed(1)};
        const float px = ${(1 / SKIN_TILE_PX).toFixed(9)};
        vec2 gx = vec2(dx, 0.0);
        vec2 gy = vec2(0.0, dy);
        float l00 = dot(textureGrad(uSkinTiles, vec3(b, layer), gx, gy).rgb, webLuma);
        float l10 = dot(textureGrad(uSkinTiles, vec3(b + vec2(px, 0.0), layer), gx, gy).rgb, webLuma);
        float l01 = dot(textureGrad(uSkinTiles, vec3(b + vec2(0.0, px), layer), gx, gy).rgb, webLuma);
        float l11 = dot(textureGrad(uSkinTiles, vec3(b + vec2(px, px), layer), gx, gy).rgb, webLuma);
        return mix(mix(l00, l10, f.x), mix(l01, l11, f.x), f.y);
      }` : ''}
      ${fog ? `${WATER_FOG}varying vec3 vSkinWorld;` : ''}
      ${fixed < 0 ? 'varying float vSkinLand;\nvarying float vSkinReef;' : ''}
      ${paint ? `uniform vec3 uExSand[4];
      uniform vec3 uExSilt[4];
      uniform vec3 uExBasalt[4];
      uniform vec3 uExCoralline[2];
      uniform vec2 uExRock;
      uniform vec2 uExSilting;
      uniform vec3 uExAdapt;
      varying float vExSlope;
      varying float vExDepth;
      varying vec3 vExWater;` : ''}
      ${coral ? `uniform vec3 uExCoral[${coral.live.length}];\nuniform vec3 uExBleached;\nvarying vec2 vExCoral;` : ''}
      ${reef ? 'uniform vec3 uRfStone[6];\nuniform vec3 uRfBasalt[5];\nuniform vec3 uRfSand[5];\nuniform vec3 uRfTurf[5];\nuniform vec3 uRfCoralline[5];\nuniform vec3 uRfRubble[4];\nvarying vec4 vExSub;\nvarying float vExRecess;' : ''}
      // A hash of a texel and a seed, 0 to 1, in integer arithmetic.
      float skinHash(vec2 texel, uint seed) {
        uvec2 v = uvec2(ivec2(texel) + 4096) * uvec2(1664525u, 1013904223u) + uvec2(seed, seed * 747796405u);
        v.x += v.y * 1664525u; v.y += v.x * 1664525u;
        v ^= v >> 16u;
        v.x += v.y * 1664525u; v.y += v.x * 1664525u;
        v ^= v >> 16u;
        return float(v.x) / 4294967295.0;
      }
      // Smooth value noise on the texel grid, 0 to 1, its lattice every cells texels (M105).
      float skinNoise(vec2 texel, float cells, uint seed) {
        vec2 p = texel / cells;
        vec2 i = floor(p);
        vec2 f = p - i;
        f = f * f * (3.0 - 2.0 * f);
        return mix(mix(skinHash(i, seed), skinHash(i + vec2(1.0, 0.0), seed), f.x), mix(skinHash(i + vec2(0.0, 1.0), seed), skinHash(i + vec2(1.0, 1.0), seed), f.x), f.y);
      }
      ${reef ? `// skinNoise and its slope along the texel grid's two axes, a unit of the noise a texel (M246: the ground's
      // small relief, its normal bent by it), from the same four hashes.
      vec3 skinNoiseD(vec2 texel, float cells, uint seed) {
        vec2 p = texel / cells;
        vec2 i = floor(p);
        vec2 f = p - i;
        vec2 d = 6.0 * f * (1.0 - f) / cells;
        f = f * f * (3.0 - 2.0 * f);
        float a = skinHash(i, seed);
        float b = skinHash(i + vec2(1.0, 0.0), seed);
        float c = skinHash(i + vec2(0.0, 1.0), seed);
        float e = skinHash(i + vec2(1.0, 1.0), seed);
        return vec3(mix(mix(a, b, f.x), mix(c, e, f.x), f.y), d.x * (b - a + (a - b - c + e) * f.y), d.y * (c - a + (a - b - c + e) * f.x));
      }
      // The smooth noise's value with a share of the texels under it, and the patches' warp's (M246): their quantiles at
      // sixteenths, MEASURED by sampling the same noise (NOISE_QUANTILES, WARP_QUANTILES), so a share of a
      // growth or of the rock is drawn as that share of the texels in blobs the noise's own shape, not in square patches.
      const float SKIN_NOISE_Q[17] = float[17](${NOISE_QUANTILES.map((v) => v.toFixed(4)).join(', ')});
      const float SKIN_WARP_Q[17] = float[17](${WARP_QUANTILES.map((v) => v.toFixed(4)).join(', ')});
      float skinNoiseAt(float share) {
        float t = clamp(share, 0.0, 1.0) * 16.0;
        int i = int(min(floor(t), 15.0));
        return mix(SKIN_NOISE_Q[i], SKIN_NOISE_Q[i + 1], t - float(i));
      }
      float skinWarpAt(float share) {
        float t = clamp(share, 0.0, 1.0) * 16.0;
        int i = int(min(floor(t), 15.0));
        return mix(SKIN_WARP_Q[i], SKIN_WARP_Q[i + 1], t - float(i));
      }` : ''}
      // A patch's draw, 0 to 1 and as even as a texel's hash (each patch one hash), for a texel: its patch about
      // cells texels across, its edges warped by the texel's smooth noise, warp, so the patches are irregular, not a
      // grid (M105, the redesign v2's "irregular, coherent patches", "no tiny high-contrast checker"); one warp for
      // every draw of a texel, as the noise is the costly part (MEASURED, its own warp for each made a frame a fifth
      // slower on a software GPU).
      float skinPatch(vec2 texel, float cells, vec2 warp, uint seed) {
        return skinHash(floor(texel / cells + warp * ${warp}), seed);
      }`)
      .replace('#include <color_fragment>', `#include <color_fragment>
      ${reef ? '// The ground\'s small relief\'s slope, world space, the normal bent by it below (M246).\n      vec3 skinBump = vec3(0.0);' : ''}
      if (uSkinStrength > 0.0) {
        vec3 skinFacing = abs(normalize(cross(dFdx(vSkinAt), dFdy(vSkinAt))));
        vec2 skinUv = skinFacing.y >= max(skinFacing.x, skinFacing.z) ? vSkinAt.xz
          : (skinFacing.x >= skinFacing.z ? vSkinAt.zy : vSkinAt.xy);
        skinUv += floor(uSkinDrift / uSkinTexel) * uSkinTexel;
        // The seafloor's choice: the drawn land's or reef's layer, or the user's sand (1).
        int skinLayer = ${fixed < 0 ? layer('reef') : fixed};
        ${floor || paint ? 'int skinOwn = -1;' : ''}
        ${fixed < 0 ? `vec2 skinTexel = floor(skinUv / uSkinTexel);
        // The texel's smooth noise, two draws a patch across: its patches' warp, and its colors' drifts.
        ${reef ? `vec3 skinWarpD = skinNoiseD(skinTexel, ${patch}, 101u);
        vec2 skinWarp = vec2(skinWarpD.x, skinNoise(skinTexel, ${patch}, 102u)) - 0.5;` : `vec2 skinWarp = vec2(skinNoise(skinTexel, ${patch}, 101u), skinNoise(skinTexel, ${patch}, 102u)) - 0.5;`}
        ${edges ? `// Its outline wandering at half a patch as well, and its patches read a texel or so from it, so their edges
        // fray in single pixels (M174).
        ${reef ? `vec3 skinBreakD = skinNoiseD(skinTexel, ${halfPatch}, 105u);
        skinWarp += ${edges.breakup.toFixed(3)} * (vec2(skinBreakD.x, skinNoise(skinTexel, ${halfPatch}, 106u)) - 0.5);` : `skinWarp += ${edges.breakup.toFixed(3)} * (vec2(skinNoise(skinTexel, ${halfPatch}, 105u), skinNoise(skinTexel, ${halfPatch}, 106u)) - 0.5);`}
        vec2 skinAt = skinTexel + ${edges.jitter.toFixed(3)} * (vec2(skinHash(skinTexel, 22u), skinHash(skinTexel, 23u)) - 0.5);` : ''}
        if (skinPatch(skinTexel, ${patch}, skinWarp, 1u) < vSkinLand) skinLayer = ${layer('land')};
        else if (${reef ? `skinWarp.x > skinWarpAt(1.0 - vSkinReef)` : `skinPatch(${at}, ${patch}, skinWarp, 2u) < vSkinReef`}) skinLayer = ${layer('reef')};
        else skinOwn = 1;` : ''}
        ${paint ? `// The exhibit's paint: a texel not the land's takes a color of its own (skinOwn 2).
        vec3 exColor = vec3(1.0);
        float rfCaustic = 1.0;
        ${reef ? reefPaint(reef, at, patch, halfPatch, warp, coral, layer('reef'), f1) : ''}
        if (skinOwn == 1) {
          skinOwn = 2;
          // Its color one of four, by the texel's smooth noise and a little of its own draw: drifts of a color,
          // dithered at their edges (M105).
          float exRock = smoothstep(uExRock.x, uExRock.y, vExSlope);
          bool exIsRock = skinPatch(${at}, ${patch}, skinWarp, 12u) < exRock;
          float exPick = 0.5 + skinWarp.x + ${PATCH_DITHER.toFixed(2)} * (skinHash(skinTexel, 15u) - 0.5);
          int exI = int(clamp(floor(exPick * 4.0), 0.0, 3.0));
          if (exIsRock) exColor = uExBasalt[exI];
          else exColor = mix(uExSand[exI], uExSilt[exI], smoothstep(uExSilting.x, uExSilting.y, vExDepth));
        } else if (skinLayer == ${layer('reef')}${reef ? ' && skinOwn != 2' : ''}) {
          // The reef's pavement: its coral's color as the data gives it, rough by texel, flecked with coralline.
          skinOwn = 2;
          // Rough in broad patches, a little by texel; the coralline in clumps (M105: patches, not flecks), as much of
          // it as the flecks were, 16% of the pavement.
          exColor = diffuseColor.rgb * (0.78 + 0.44 * (0.75 * (0.5 + skinWarp.y) + 0.25 * skinHash(skinTexel, 16u)));
          ${coral ? `// Its clumps' edges warped at their own size too, so a clump is a blob, not a square (M125: the
          // patch's warp alone left them square at half its size).
          vec2 exWarp = skinWarp + vec2(skinNoise(skinTexel, ${halfPatch}, 103u), skinNoise(skinTexel, ${halfPatch}, 104u)) - 0.5;` : 'vec2 exWarp = skinWarp;'}
          float exPick = skinPatch(${at}, ${halfPatch}, exWarp, 13u);
          if (exPick > ${(1 - CORALLINE_SHARE).toFixed(2)}) exColor = uExCoralline[exPick > 0.93 ? 1 : 0];
          ${coral ? `// Its own coral (M125): clumps half a patch across over its share, each clump one live color, white
          // over the bleached share of them, a little rough by texel; a clump's draw its own, apart from the coralline's.
          else if (skinPatch(${at}, ${halfPatch}, exWarp, 17u) < vExCoral.x) {
            ${coralClump(at, halfPatch, warp, coral)}
          }` : ''}
        }` : ''}
        // The mip level from the footprint in the scene's own space, the same whichever way a face
        // projects: per pixel the projection can change between two triangles of a 2-by-2 block,
        // and the texture's own derivatives would then jump by a tile and draw a crease plain.
        float skinDx = length(dFdx(vSkinAt)) * uSkinScale;
        float skinDy = length(dFdy(vSkinAt)) * uSkinScale;
        ${own ? `// The user's own tile, at its own size: its footprint taken as the drawn tiles' is.
        vec2 skinOwnDx = vec2(length(dFdx(vSkinAt)) * uSkinOwnScale.x, 0.0);
        vec2 skinOwnDy = vec2(0.0, length(dFdy(vSkinAt)) * uSkinOwnScale.y);
        vec3 skinTile = textureGrad(uSkinOwn, skinUv * uSkinOwnScale, skinOwnDx, skinOwnDy).rgb;
        diffuseColor.rgb *= mix(vec3(1.0), skinTile / max(uSkinOwnMean, vec3(0.0001)), uSkinStrength);
        ${options.alpha ? `const vec3 skinLuma = vec3(0.2126, 0.7152, 0.0722);
        diffuseColor.a *= mix(1.0, dot(skinTile, skinLuma) / max(dot(uSkinOwnMean, skinLuma), 0.0001), uSkinStrength);` : ''}` : `vec3 skinTile = textureGrad(uSkinTiles, vec3(skinUv * uSkinScale, float(skinLayer)), vec2(skinDx, 0.0), vec2(0.0, skinDy)).rgb;
        ${paint ? `${reef ? `// The water's light on the reef's materials its color partly taken out (M177: the user's "the final rendered
        // scene to retain the warm rock and sand tones while still clearly looking underwater"), its strength kept; the
        // veil still blues what lies farther.
        vec3 rfLight = vExWater / uExAdapt;
        rfLight = mix(rfLight, vec3(dot(rfLight, vec3(0.2126, 0.7152, 0.0722))), ${f1(reef.balance)});
        if (skinOwn == 2) diffuseColor.rgb = exColor * rfLight;` : `if (skinOwn == 2) diffuseColor.rgb = exColor * vExWater / uExAdapt;`}
        else diffuseColor.rgb *= mix(vec3(1.0), skinTile / max(uSkinMeans[skinLayer], vec3(0.0001)), uSkinStrength) * vExWater / uExAdapt;` : ''}
        ${floor ? `vec3 skinMean = uSkinMeans[skinLayer];
        // The user's sand at its own size, its footprint the drawn tiles' on its scale.
        if (skinOwn == 1) {
          skinTile = textureGrad(uSkinSand, skinUv * uSkinSandScale, vec2(skinDx / uSkinScale * uSkinSandScale.x, 0.0), vec2(0.0, skinDy / uSkinScale * uSkinSandScale.y)).rgb;
          skinMean = uSkinSandMean;
        }
        diffuseColor.rgb *= mix(vec3(1.0), skinTile / max(skinMean, vec3(0.0001)), uSkinStrength);` : paint ? '' : `diffuseColor.rgb *= mix(vec3(1.0), skinTile / max(uSkinMeans[skinLayer], vec3(0.0001)), uSkinStrength);`}
        ${options.alpha ? `const vec3 skinLuma = vec3(0.2126, 0.7152, 0.0722);
        diffuseColor.a *= mix(1.0, dot(skinTile, skinLuma) / max(dot(uSkinMeans[skinLayer], skinLuma), 0.0001), uSkinStrength);` : ''}`}
        ${caustics ? `// Caustics: two copies of the web, sliding apart, from above; their mean brightness above or below
        // the tile's, as a share of the white lines' (luma 1) above it, so both copies white lift the floor by
        // the contrast and on average they light nothing; by the light that reaches here, under the water.
        if (vCaustic > 0.0 && vSkinAt.y < uCausticSurface) {
          const vec3 causticLuma = vec3(0.2126, 0.7152, 0.0722);
          vec2 causticAt = vSkinAt.xz;
          // The skin's own footprint, taken outside this branch: inside it, which pixels run depends on
          // vCaustic, and a screen-space derivative there is undefined.
          ${causticOwn ? `const float causticTexel = ${causticTexel.toFixed(6)};
          const float causticScale = ${(1 / (causticTexel * SKIN_TILE_PX)).toFixed(9)};
          float causticDx = skinDx * ${(options.texel / causticTexel).toFixed(6)};
          float causticDy = skinDy * ${(options.texel / causticTexel).toFixed(6)};` : `float causticTexel = uSkinTexel;
          float causticScale = uSkinScale;
          float causticDx = skinDx;
          float causticDy = skinDy;`}
          // Slid by their whole drift, not in whole texels (M227: stepped a texel at a time along each axis, each of the
          // Reef's two webs jumped 7.5 cm about 4.7 times a second).
          vec2 causticA = (causticAt + uCausticDrift.xy) * causticScale;
          vec2 causticB = (causticAt + uCausticDrift.zw) * causticScale;
          float causticLayer = float(${layer('caustic')});
          float causticLight = 0.5 * (causticWeb(causticA, causticLayer, causticDx, causticDy) + causticWeb(causticB, causticLayer, causticDx, causticDy));
          float causticMean = dot(uSkinMeans[${layer('caustic')}], causticLuma);
          ${vary ? `// Brighter and dimmer over the floor as the surface's waves focus more or less of the light (M174), the
          // variation still while the web drifts through it (M174's review: drifting with the web's own offset, which
          // wraps a tile at a time, it jumped 1.7 m at each wrap); on whole texels, still, as the floor's patches are.
          float causticVary = 1.0 + ${vary.amount.toFixed(3)} * (2.0 * skinNoise(floor(causticAt / causticTexel), ${vary.texels.toFixed(1)}, 107u) - 1.0);` : 'const float causticVary = 1.0;'}
          diffuseColor.rgb *= 1.0 + uCausticContrast * causticVary${paint ? ' * rfCaustic' : ''} * vCaustic * (causticLight - causticMean) / max(1.0 - causticMean, 0.0001);
        }` : ''}
      }`);
    if (reef) {
      // The ground's small relief bends its normal (M246), so the key light, the hour's sun or the moon, lights it.
      shader.fragmentShader = shader.fragmentShader.replace('#include <normal_fragment_maps>', `#include <normal_fragment_maps>
      normal = normalize(normal - (viewMatrix * vec4(skinBump, 0.0)).xyz);`);
    }
    if (fog) {
      shader.fragmentShader = shader.fragmentShader.replace('#include <opaque_fragment>', `// Fog: the light faded toward the water's color by how much water the eye looks through to
      // this point (\`WATER_FOG\`), before it is written out.
      vec4 skinFog = waterFog(vSkinWorld);
      outgoingLight = mix(outgoingLight, skinFog.rgb, skinFog.a);
      #include <opaque_fragment>`);
    }
  };
  material.customProgramCacheKey = () => `skin${own ? 'o' : fixed}${floor ? 'l' : ''}${paint ? `p${patch}w${warp}${coral ? `k${coral.live.length}` : ''}${edges ? `e${edges.breakup}j${edges.jitter}` : ''}${vary ? `v${vary.amount}t${vary.texels}` : ''}${reef ? `r${JSON.stringify({ ...reef, limestone: 0, basalt: 0, sand: 0, turf: 0, coralline: 0, rubble: 0 })}` : ''}` : ''}${options.alpha ? 'a' : ''}${caustics ? `c${causticOwn ? `${causticTexel}/${options.texel}` : ''}` : ''}${fog ? 'f' : ''}`;
  material.needsUpdate = true;
  return { ...uniforms, ...caustics, ...fog, ...(paintUniforms ? { uExAdapt: paintUniforms.uExAdapt } : {}) };
}
